/**
 * KNFacadePlugin (P1-T06): makes the trim atlas read like real facades.
 *
 * The generator's UVs put one 4 m x 3.5 m module at U=0..1 and 8 storey rows
 * down V (row 0 = shopfront). Window bays sit at fixed module positions
 * (see make_trim_atlas.py), so this plugin rebuilds a window mask from
 * fract(vAlbedoUV) — no extra geometry or textures:
 *
 *  - day:   interior-mapped room behind each window (module-space box
 *           projection: parallax changes as you move, rooms look deep)
 *  - night: deterministic per-window occupancy from a hash of
 *           (building, module, storey): warm lamps, TV flicker, some dark;
 *           shopfronts glow.
 *  - always: +-8 % albedo tint + roughness jitter per ~16 m block, so the
 *           tiled atlas stops looking tiled (realism rule: no two identical
 *           facades).
 *
 * Hook names verified against the babylonjs 9.29.0 bundle (present in both
 * the GLSL and the WebGPU-transcoded chunks): code injected at
 * CUSTOM_FRAGMENT_UPDATE_ALBEDO sees `surfaceAlbedo`, at
 * UPDATE_METALLICROUGHNESS sees `metallicRoughness`, and at
 * CUSTOM_FRAGMENT_BEFORE_FOG sees `finalColor` after emissive composition.
 * Shared window data is computed once at the ALBEDO hook and reused
 * downstream (same main() scope). GLSL kept transcoder-safe: pure functions,
 * no out-parameters.
 */
import { MaterialPluginBase, type Material, type MaterialDefines } from 'babylonjs';
import { CityEnv } from './cityEnv';

// Helper functions at global scope via CUSTOM_FRAGMENT_DEFINITIONS — in the
// GLSL pbr fragment that token sits between the lighting-function includes
// and the pbrBlock* includes (verified in the 9.29 bundle), i.e. above
// main() and below the #define block, so KN_FACADE is already defined.
// (getUniforms().fragment is NOT emitted by PBRMaterial's code generator on
// 9.29 — the ADDITIONAL_FRAGMENT_DECLARATION token is absent from its
// compiled shaders; UBO uniform declarations DO come from getUniforms().ubo.)
const DECL = `
#ifdef KN_FACADE
float knHash1(float p) {
  p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p);
}
float knHash2(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
// window mask from module UV (u = 4 m module, v fract over one 8-storey
// atlas repeat). fract() anchors row 0 (shopfront) at the building base
// even with the per-building density scales applied upstream. Bays per
// make_trim_atlas.py: glass recess x 0.155..0.295 and 0.475..0.65,
// y 0.243..0.743 of the row; shopfront x 0.15..0.85, y 0.10..0.83 of row 0.
vec4 knWindowMask(vec2 modUv) {
  float rowF = modUv.y * 8.0;
  float storey = floor(rowF);
  float f = fract(rowF);
  if (storey < 0.5) {
    if (modUv.x > 0.15 && modUv.x < 0.85 && f > 0.10 && f < 0.83) {
      return vec4(1.0, (modUv.x - 0.15) / 0.7, (f - 0.10) / 0.73, 1.0);
    }
    return vec4(0.0);
  }
  bool bayA = modUv.x > 0.155 && modUv.x < 0.295;
  bool bayB = modUv.x > 0.5125 && modUv.x < 0.6125;
  float y0 = bayA ? 0.243 : 0.286;
  float y1 = bayA ? 0.743 : 0.8;
  if ((bayA || bayB) && f > y0 && f < y1) {
    float u0 = bayA ? 0.155 : 0.5125;
    float uw = bayA ? 0.14 : 0.1;
    return vec4(1.0, (modUv.x - u0) / uw, (f - y0) / (y1 - y0), 0.0);
  }
  return vec4(0.0);
}
// procedural room interior behind the glass: walls, floor/ceiling bands,
// furniture silhouette, blinds; depth 0..1 = how far into the room we see
vec3 knInterior(vec2 winUv, float depth, vec2 seedP) {
  // day-bright room: mid-tone walls so windows read as open, not punched out
  float wall = 0.42 + 0.22 * knHash2(seedP + 7.31);
  vec3 c = vec3(wall) * mix(vec3(1.0, 0.94, 0.84), vec3(0.82, 0.88, 1.0), knHash2(seedP + 2.7));
  float floorL = smoothstep(0.18, 0.14, winUv.y);
  float ceilL = smoothstep(0.86, 0.92, winUv.y);
  c = mix(c, vec3(0.16, 0.12, 0.10), floorL);      // dark floor band (depth cue)
  c = mix(c, vec3(0.5, 0.48, 0.44), ceilL);        // lit ceiling strip
  float fn = step(0.55, knHash2(seedP + 11.77));
  float box = step(0.30, winUv.x) * step(winUv.x, 0.62) * step(0.16, winUv.y) * step(winUv.y, 0.42);
  c = mix(c, vec3(0.10, 0.09, 0.12), box * fn);    // furniture silhouette
  float curt = step(knHash2(seedP + 4.44), 0.3) * smoothstep(0.72, 0.9, winUv.y);
  c = mix(c, vec3(0.62, 0.58, 0.52), curt);        // blinds top
  return c * (0.45 + 0.55 * depth);
}
// occupancy hash -> (lit 0..1, hash, tv flag); seed = building+module+storey
vec3 knOccupancy(vec3 cell) {
  float h = knHash2(cell.xy + cell.z * 7.13 + 0.5);
  float on = step(0.45, h);
  float tv = step(0.82, h);
  float flick = mix(1.0, 0.75 + 0.25 * sin(knDayTime * 9.0 + h * 44.0), tv);
  float level = on * (0.45 + 0.55 * knHash2(cell.zy + 1.9)) * flick;
  return vec3(level, h, tv);
}
#endif
`;

// Shared per-pixel state: declared as mutable GLSL globals next to the
// helpers (the ALBEDO / METALLICROUGHNESS / BEFORE_FOG hooks live in
// different brace scopes inside pbr main(), so locals don't reach them).
// Assigned once at the ALBEDO hook, read by the later hooks.
const FACADE_GLOBALS = `
#ifdef KN_FACADE
vec2 knBlk;
float knJit;
float knMU;    // horizontal module scale (window density per building)
float knVU;    // vertical storey scale (storey height per building)
float knWarm;  // 0..1 warm/cool wall hue mix
vec2 knModUv;
vec4 knWin;
vec3 knCell;
vec3 knOcc;
#endif
`;

const FACADE_ASSIGN = `
#ifdef KN_FACADE
knBlk = floor(vPositionW.xz / 16.0);
// building-level seed: blocks are separated by >=18 m streets, so a 26 m
// grid almost never straddles two buildings; adjacent narrow shops sharing
// a seed is acceptable
vec2 knBld = floor(vPositionW.xz / 26.0);
float hs = knHash2(knBld);
knMU = 0.75 + floor(hs * 3.0) * 0.25;          // 0.75 / 1.0 / 1.25 bays per 4 m
knVU = 0.8 + floor(knHash2(knBld + 9.7) * 3.0) * 0.225; // 0.8 / 1.025 / 1.25 storey scale
knWarm = knHash2(knBld + 5.3);
knJit = knHash2(knBlk) * 0.16 - 0.08;
knModUv = vec2(fract(vAlbedoUV.x * knMU), fract(vAlbedoUV.y * knVU));
knWin = knWindowMask(knModUv);
knCell = vec3(floor(vPositionW.x / 4.0), floor(knModUv.y * 8.0) + 0.5 * floor(vPositionW.z / 4.0), knBld.x + knBld.y * 57.0);
knOcc = knWin.x > 0.5 ? knOccupancy(knCell) : vec3(0.0);
#endif
`;

const ALBEDO = `
#ifdef KN_FACADE
{
  surfaceAlbedo *= (1.0 + knJit) * mix(vec3(1.03, 0.98, 0.92), vec3(0.93, 0.97, 1.06), knWarm);
  if (knWin.x > 0.5) {
    vec2 winUv = knWin.yz;
    float shop = knWin.w;
    // module-space box projection: room 3 m deep behind the pane
    vec3 V = normalize(vEyePosition.xyz - vPositionW); // vEyePosition is vec4 in the Scene UBO
    float u0 = knModUv.x < 0.4 ? 0.155 : 0.475;
    vec2 wMin = mix(vec2(u0, 0.243), vec2(0.15, 0.10), shop);
    vec2 wMax = mix(vec2(u0 + 0.14, 0.743), vec2(0.85, 0.83), shop);
    vec2 p = mix(wMin, wMax, winUv);
    vec3 ray = vec3((p - vec2(0.5, 0.5)) * vec2(4.0, 3.2), -3.0);
    float vz = max(-V.z, 0.15);
    vec3 hit = vec3(0.5, 0.5, 0.0) + (ray / vz) * 0.5;
    float par = clamp(1.0 - length(hit.xy - 0.5) * 1.1, 0.0, 1.0);
    vec3 room = knInterior(winUv, 0.35 + 0.65 * par, knCell.xy + 3.1) * mix(vec3(1.05, 0.99, 0.92), vec3(0.94, 0.98, 1.07), knWarm);
    // day glass: visible room + sky sheen rising up the pane (depth cue)
    vec3 sheen = vec3(0.30, 0.40, 0.55) * smoothstep(0.15, 1.0, winUv.y) * 0.45;
    vec3 dayC = room * (0.9 + 0.8 * par) + sheen;
    vec3 warmCol = mix(vec3(1.0, 0.72, 0.42), vec3(0.55, 0.7, 1.0), knOcc.z);
    vec3 nightC = room * 0.25 + warmCol * knOcc.x * (0.55 + 0.45 * par);
    vec3 glass = mix(dayC, nightC, smoothstep(0.12, 0.5, knNight));
    float dust = knHash1(floor(winUv.x * 8.0) + knCell.z * 13.0) * 0.05;
    surfaceAlbedo = mix(surfaceAlbedo, glass + dust, 0.92);
  }
}
#endif
`;

const ROUGH = `
#ifdef KN_FACADE
{
  if (knWin.x > 0.5) {
    metallicRoughness.g = 0.12;   // glass: smooth
    metallicRoughness.r = 0.15;   // mostly dielectric: interiors stay visible,
                                  // gloss comes from roughness, not env mirror
  } else {
    metallicRoughness.g = clamp(metallicRoughness.g + knJit, 0.05, 1.0);
  }
}
#endif
`;

const EMISSIVE = `
#ifdef KN_FACADE
{
  if (knNight > 0.05 && knWin.x > 0.5) {
    vec3 warmCol2 = mix(vec3(1.0, 0.72, 0.42), vec3(0.55, 0.7, 1.0), knOcc.z);
    float shopGlow = knWin.w * 0.9;   // shopfronts always glow at night
    finalColor.rgb += warmCol2 * max(knOcc.x, shopGlow) * knNight * mix(0.34, 0.55, knWin.w);
  }
}
#endif
`;

export class FacadePlugin extends MaterialPluginBase {
  constructor(material: Material) {
    super(material, 'KNFacade', 202, { KN_FACADE: false }, true, true);
  }
  override prepareDefines(defines: MaterialDefines): void {
    (defines as unknown as Record<string, boolean>).KN_FACADE = true;
  }
  override getClassName(): string {
    return 'KNFacadePlugin';
  }
  override getUniforms(): {
    ubo: { name: string; size: number; type: string }[];
    fragment: string;
  } {
    return {
      ubo: [
        { name: 'knNight', size: 1, type: 'float' },
        { name: 'knDayTime', size: 1, type: 'float' },
      ],
      fragment: DECL,
    };
  }
  override bindForSubMesh(ubo: { updateFloat(name: string, v: number): void }): void {
    ubo.updateFloat('knNight', CityEnv.night);
    ubo.updateFloat('knDayTime', CityEnv.time);
  }
  override getCustomCode(shaderType: string): Record<string, string> | null {
    if (shaderType !== 'fragment') return null;
    // Helper declarations live in getUniforms().fragment (single copy;
    // declaring them here too would duplicate them and break compilation).
    return {
      CUSTOM_FRAGMENT_DEFINITIONS: DECL + FACADE_GLOBALS,
      CUSTOM_FRAGMENT_UPDATE_ALBEDO: FACADE_ASSIGN + ALBEDO,
      CUSTOM_FRAGMENT_UPDATE_METALLICROUGHNESS: ROUGH,
      CUSTOM_FRAGMENT_BEFORE_FOG: EMISSIVE,
    };
  }
}
